/**
 * What makes a newsletter page an *issue*.
 *
 * An issue is an ordinary written page — `/newsletter/2026-09` is a
 * `WrittenPage` in `content/pages/` with its own `RouteDef`, like every other
 * page on the site, and the AI editor writes it the way it writes anything
 * else. The only extra thing it needs is a line saying "this one belongs in the
 * archive, and it is dated X", and that is this.
 *
 * ONE FILE PER ISSUE, and this descriptor lives in it. The alternative — a
 * catalogue file listing the issues beside the folder holding their pages — is
 * the shape `content/ebooks/` has, and it costs two things this does not:
 * a catalogue entry can name a page that does not exist (`EBOOKS_NEEDING_PAGE`
 * is the guard that had to be written for it), and the title would be written
 * twice and drift. Here the title, the summary and the date all come off the
 * page itself.
 *
 * Deliberately in its own module rather than in `newsletterIssues.ts`: that
 * module globs `content/pages/*.ts` eagerly, so an issue page importing it
 * would be importing a module that is mid-initialisation when its own file is
 * evaluated — a `ReferenceError` at prerender rather than a typecheck error.
 * `content/ebooks.ts` carries the same warning for the same reason. Import the
 * TYPE from here; nothing in here reads the folder.
 */

export interface NewsletterIssue {
  /**
   * The route id of the issue's own page — the key into `Dictionary['pages']`.
   *
   * Not a path and not a title: the path comes from the page's own `RouteDef`
   * and the words come from the dictionary, so the archive card cannot
   * disagree with the page it opens, and a Turkish reader gets the Turkish
   * title through the ordinary English-underneath merge with nothing restated
   * here.
   */
  page: string
  /**
   * Publication date, `YYYY-MM-DD`. Orders the archive, newest first, and
   * dates the card.
   *
   * A string rather than a `Date` because this is content: it is written by
   * hand (and by the editor agent) into a file, it is compared as text, and a
   * `new Date('2026-09-11')` in a module that both Node and the browser
   * evaluate is a timezone question nobody asked. `newsletterIssues.ts`
   * refuses a value that is not this shape, at module load, so a typo fails
   * the build rather than sorting strangely.
   */
  date: string
}

/**
 * Is this export an issue descriptor?
 *
 * A shape check, like `isWrittenPage` in `content/pageRegistry.ts`, and for the
 * same reason: the page modules are discovered by glob and everything they
 * export is offered. Two fields is enough to be unambiguous — a `RouteDef` has
 * `id`/`path` and a `WrittenPage` has `sections`, so neither can be mistaken
 * for this and this cannot be mistaken for either.
 */
export function isNewsletterIssue(value: unknown): value is NewsletterIssue {
  if (typeof value !== 'object' || value === null) return false
  const v = value as Record<string, unknown>
  return typeof v.page === 'string' && typeof v.date === 'string'
}
